import type { SelectOption } from '@components';
import LogRocket from 'logrocket';

import type { ListURLParams, QueryRouteBarrelTypes } from '@core/api';
import { hasValue } from '@core/utils/equality';
import type { ExtractEntityTypeFromBarrel } from '@core/utils/lookupQuery';

export type RouteHelpers<QKey extends keyof QueryRouteBarrelTypes> = {
  /** Builds the lookup's list query, typed against the barrel's queryParams. */
  constructQueryParams: <
    T extends ExtractEntityTypeFromBarrel<QKey> = ExtractEntityTypeFromBarrel<QKey>,
  >(
    inputText: string,
    params?: Partial<ListURLParams<T>>,
  ) => { queryParams: ListURLParams<T> };
};

/**
 * `AlertDupeOptions` will alert LogRocket if there are any duplicate
 * `value`s inside of options.
 */
export const AlertDupeOptions = (options: SelectOption<unknown>[]): void => {
  // If there are duplicate `value`s, alert LogRocket as this will affect user experience.
  const optVals = options.map((o) => o.value);
  const dupeVals = optVals.filter((value, index, self) => self.indexOf(value) !== index);
  if (dupeVals.length !== 0) {
    LogRocket.error(
      'Duplicate option values found. This may show users unintended lookup values.',
      dupeVals,
    );
  }
};

export const ErrorOption: SelectOption<string> = {
  label: 'Error loading options',
  value: 'Error',
  isDisabled: true,
};

export const LoadingOption: SelectOption<string> = {
  label: 'Loading...',
  value: 'Loading',
  isDisabled: true,
};

/** A formValueMatcher for domain objects: Formik state holds an array of { id: string }
 * and the lookup options' values are string IDs. */
export const ReferenceValueMatcher = (
  formValue: { id: string }[],
  options: SelectOption<string>[],
): SelectOption<string>[] => {
  const optionsByValue = new Map(options.map((opt) => [opt.value, opt]));
  return formValue.map((v) => optionsByValue.get(v.id)).filter((opt) => opt !== undefined);
};

/** A formValueMatcher for a Formik field that holds the chosen options' string IDs. */
export const stringIdFormValueMatcher = (
  formValue: string[],
  options: SelectOption<string>[],
): SelectOption<string>[] => {
  const optionsByValue = new Map(options.map((opt) => [opt.value, opt]));
  return formValue.map((id) => optionsByValue.get(id)).filter(hasValue);
};

/**
 * Converts an array of options to the expected Formik value structure for an
 * array of YupReferences.
 */
export const OptionsToReferences = (
  options: SelectOption<string>[],
): {
  id: string | null;
  option: SelectOption<string> | null;
}[] => options.map((option) => ({ id: option.value, option }));
